import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { X, MagnifyingGlass } from "@phosphor-icons/react";
import { normalizeText } from "@/lib/text";

/**
 * Combobox / autocomplete for patients.
 * - `patients`: [{ patient_id, first_name, last_name, email }]
 * - `value`: currently selected patient_id (or "")
 * - `onChange(patient_id)`
 */
export default function PatientAutocomplete({ patients, value, onChange, placeholder = "Nom, prénom…" }) {
  const selected = patients.find((p) => p.patient_id === value) || null;
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // When selected patient changes externally (or on mount), keep the input synced.
  useEffect(() => {
    setQuery(selected ? `${selected.first_name} ${selected.last_name}` : "");
  }, [selected?.patient_id]); // eslint-disable-line

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const results = useMemo(() => {
    const q = normalizeText(query.trim());
    // If the input matches exactly the currently selected patient's display, show full list.
    const isDisplayOfSelected = selected && q === normalizeText(`${selected.first_name} ${selected.last_name}`);
    if (!q || isDisplayOfSelected) return patients.slice(0, 50);
    return patients
      .filter((p) => normalizeText(`${p.first_name} ${p.last_name}`).includes(q))
      .slice(0, 50);
  }, [query, patients, selected]);

  const pick = (p) => {
    onChange(p.patient_id);
    setQuery(`${p.first_name} ${p.last_name}`);
    setOpen(false);
  };

  const clear = () => {
    onChange("");
    setQuery("");
    setOpen(true);
    inputRef.current?.focus();
  };

  const onKey = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) { setOpen(true); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (results[hi]) pick(results[hi]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="relative w-full" ref={wrapRef}>
      <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5F758F] pointer-events-none" />
      <Input
        ref={inputRef}
        data-testid="patient-autocomplete-input"
        value={query}
        placeholder={placeholder}
        onFocus={() => { setOpen(true); setHi(0); }}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setHi(0);
          if (selected && e.target.value !== `${selected.first_name} ${selected.last_name}`) onChange("");
        }}
        onKeyDown={onKey}
        className="bg-white border-[#E1E9F0] pl-9 pr-9 h-11"
        autoComplete="off"
      />
      {query && (
        <button
          type="button"
          aria-label="Effacer"
          onClick={clear}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-[#5F758F] hover:bg-[#F4F7FB] hover:text-[#1E3A5F]"
          data-testid="patient-autocomplete-clear"
        >
          <X size={14} />
        </button>
      )}

      {open && (
        <div
          className="absolute z-40 mt-1 w-full max-h-72 overflow-auto rounded-md border border-[#E1E9F0] bg-white shadow-lg"
          data-testid="patient-autocomplete-list"
        >
          {results.length === 0 ? (
            <div className="px-3 py-3 text-sm text-[#5F758F]">Aucun patient ne correspond.</div>
          ) : (
            results.map((p, idx) => (
              <button
                type="button"
                key={p.patient_id}
                onMouseDown={(e) => { e.preventDefault(); pick(p); }}
                onMouseEnter={() => setHi(idx)}
                className={`w-full text-left px-3 py-2 text-sm flex flex-col ${
                  idx === hi ? "bg-[#F4F7FB] text-[#3A8FA5]" : "text-[#1E3A5F] hover:bg-[#F4F7FB]"
                }`}
                data-testid={`patient-opt-${p.patient_id}`}
              >
                <span className="font-medium leading-snug">{p.first_name} {p.last_name}</span>
                {p.date_of_birth && (
                  <span className="text-xs text-[#5F758F] leading-snug">
                    {new Date(p.date_of_birth).toLocaleDateString("fr-FR")}
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
